/**
 * 學習端入口整併(Learn IA Consolidation,boss 選項 2,spec
 * docs/spec/LEARN_IA_CONSOLIDATION.md「★ 定案」)。
 *
 * 把舊「8 個相似入口 + 兩個重複首頁」收斂成單一學習首頁:
 *   學 / 練 / 複習  ＋  拍考卷(降一階全寬工具)= 首頁恆 4 個 tap target。
 *
 * 本檔 = 四門的「身分常數」(icon / 門色 / 副標)+ 門卡 / 工具鈕的 DOM 工廠,
 * 讓首頁與各門內頁共用同一套視覺語彙。每門 icon、色相兩兩互異(guardrail #2),
 * 副標 ≤12 字、孩子詞(guardrail #6)。視覺仍是學術風(紙感 / serif / 墨色),
 * 門色只當「左書脊 + icon 徽章」的低飽和強調。
 *
 * 學習/遊戲分離鐵則:此層只 navigate,不碰 commanderXp / 戰鬥數值。
 */

import { ensureAcademicTheme } from '../ui/theme';

/** 四門 id —— 與 main.ts mode / 測試 / 路由依賴,不可改字串。 */
export type DoorId = 'learn' | 'practice' | 'review' | 'capture';

/** 一個門的識別常數。 */
export interface DoorSpec {
  id: DoorId;
  /** 首頁卡 DOM id（測試 / QA 依賴)。 */
  domId: string;
  icon: string;
  /** 門名(serif 大字)。 */
  label: string;
  /** 副標(≤12 字、孩子詞,guardrail #6)。 */
  sub: string;
  /** 門色(低飽和學術色,4 門兩兩互異,guardrail #2)。 */
  accent: string;
  /** icon 徽章底色(門色淡 tint)。 */
  tint: string;
}

/**
 * 四門定案(spec「★ 定案」表)。色相 0 重複:
 *   學=墨綠 / 練=靛藍 / 複習=琥珀 / 拍=灰。icon 0 重複。
 */
export const DOORS: Readonly<Record<DoorId, DoorSpec>> = {
  learn: {
    id: 'learn',
    domId: 'door-learn',
    icon: '📖',
    label: '學',
    sub: '看課本、學每一課',
    accent: '#1f6f5c', // 墨綠(沿用 --ef-green)
    tint: 'rgba(31, 111, 92, 0.10)',
  },
  practice: {
    id: 'practice',
    domId: 'door-practice',
    icon: '✏️',
    label: '練',
    sub: '自己挑一張來寫',
    accent: '#2f5b8f', // 靛藍
    tint: 'rgba(47, 91, 143, 0.10)',
  },
  review: {
    id: 'review',
    domId: 'door-review',
    icon: '🔁',
    label: '複習',
    sub: '我寫錯的、該回頭的',
    accent: '#b07a2e', // 琥珀
    tint: 'rgba(176, 122, 46, 0.12)',
  },
  capture: {
    id: 'capture',
    domId: 'door-capture',
    icon: '📷',
    label: '拍考卷',
    sub: '拍考卷 / 拍課本',
    accent: '#6b6b63', // 灰
    tint: 'rgba(107, 107, 99, 0.10)',
  },
};

/** 三個主門的固定順序(學 → 練 → 複習)。拍考卷降一階單獨處理。 */
export const MAIN_DOOR_ORDER: ReadonlyArray<DoorId> = ['learn', 'practice', 'review'];

/** 門卡可覆寫的動態副標(例:複習門帶到期數)。 */
export interface DoorCardOptions {
  /** 覆寫 spec 預設副標。 */
  sub?: string;
  onClick: () => void;
}

/**
 * 建一張「門卡」（首頁三主門用)。左書脊 + icon 徽章 + serif 門名 + 副標 + chevron。
 * 鍵盤可用(role=button / tabindex / Enter|Space)。
 */
export function doorCard(spec: DoorSpec, opts: DoorCardOptions): HTMLElement {
  ensureAcademicTheme();
  const card = document.createElement('div');
  card.className = 'ef-door';
  card.id = spec.domId;
  card.setAttribute('role', 'button');
  card.setAttribute('tabindex', '0');
  card.setAttribute('data-door', spec.id);
  card.style.setProperty('--door-accent', spec.accent);
  card.style.setProperty('--door-tint', spec.tint);
  const sub = opts.sub ?? spec.sub;
  card.setAttribute('aria-label', `${spec.label}:${sub}`);

  const badge = document.createElement('div');
  badge.className = 'ef-door__badge';
  badge.setAttribute('aria-hidden', 'true');
  badge.textContent = spec.icon;

  const body = document.createElement('div');
  body.className = 'ef-door__body';
  const label = document.createElement('div');
  label.className = 'ef-door__label';
  label.textContent = spec.label;
  const subEl = document.createElement('div');
  subEl.className = 'ef-door__sub';
  subEl.textContent = sub;
  body.append(label, subEl);

  const chevron = document.createElement('div');
  chevron.className = 'ef-door__chevron';
  chevron.setAttribute('aria-hidden', 'true');
  chevron.textContent = '›';

  card.append(badge, body, chevron);

  const fire = (): void => opts.onClick();
  card.addEventListener('click', fire);
  card.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fire();
    }
  });
  return card;
}

/** 建「拍考卷」降一階全寬工具鈕(灰、虛線框,非與三門並列主軸)。 */
export function captureToolButton(opts: { onClick: () => void }): HTMLButtonElement {
  ensureAcademicTheme();
  const spec = DOORS.capture;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'ef-tool-wide';
  btn.id = spec.domId;
  btn.setAttribute('data-door', spec.id);
  btn.setAttribute('aria-label', `${spec.label}:${spec.sub}`);

  const badge = document.createElement('span');
  badge.className = 'ef-tool-wide__badge';
  badge.setAttribute('aria-hidden', 'true');
  badge.textContent = spec.icon;

  const body = document.createElement('span');
  body.style.cssText = 'display:flex;flex-direction:column;gap:2px;min-width:0;';
  const label = document.createElement('span');
  label.className = 'ef-tool-wide__label';
  label.textContent = spec.label;
  const sub = document.createElement('span');
  sub.className = 'ef-tool-wide__sub';
  sub.textContent = spec.sub;
  body.append(label, sub);

  btn.append(badge, body);
  btn.addEventListener('click', opts.onClick);
  return btn;
}
